import React, { useState } from 'react'

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  billingPeriod: 'monthly' | 'yearly'
  features: string[]
  limits: {
    products: number | 'unlimited'
    featuredProducts: number
    images: number | 'unlimited'
    analytics: boolean
    prioritySupport: boolean
    customBranding: boolean
    salePromotions: number
  }
  popular?: boolean
  color: string
}

const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'basic',
    name: 'Basic Listing',
    price: 29,
    billingPeriod: 'monthly',
    color: '#6c757d',
    features: [
      'Basic vendor profile',
      'Up to 10 product listings',
      'Standard search visibility',
      'Customer reviews display',
      'Basic analytics dashboard'
    ],
    limits: {
      products: 10,
      featuredProducts: 0,
      images: 50,
      analytics: true,
      prioritySupport: false,
      customBranding: false,
      salePromotions: 1
    }
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 79,
    billingPeriod: 'monthly',
    color: '#2c5530',
    popular: true,
    features: [
      'Enhanced vendor profile',
      'Up to 50 product listings',
      'Priority search placement',
      'Featured product slots (3)',
      'Advanced analytics & insights',
      'Customer messaging system',
      'Sale promotion tools'
    ],
    limits: {
      products: 50,
      featuredProducts: 3,
      images: 200,
      analytics: true,
      prioritySupport: true,
      customBranding: false,
      salePromotions: 5
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 149,
    billingPeriod: 'monthly',
    color: '#ffa726',
    features: [
      'Premium vendor profile',
      'Unlimited product listings',
      'Top search priority',
      'Unlimited featured products',
      'Full analytics suite',
      'Priority customer support',
      'Custom branding options',
      'Unlimited sale promotions',
      'API access for inventory sync'
    ],
    limits: {
      products: 'unlimited',
      featuredProducts: 999,
      images: 'unlimited',
      analytics: true,
      prioritySupport: true,
      customBranding: true,
      salePromotions: 999
    }
  }
]

interface VendorSubscriptionPlansProps {
  currentPlan?: string
  onSelectPlan?: (planId: string) => void
  showCurrentPlan?: boolean
}

export default function VendorSubscriptionPlans({ 
  currentPlan, 
  onSelectPlan, 
  showCurrentPlan = true 
}: VendorSubscriptionPlansProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  const getAnnualPrice = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 12 * 0.83) // 17% discount for annual
  }

  const handlePlanSelect = (planId: string) => {
    onSelectPlan?.(planId)
  }

  return (
    <div className="vendor-subscription-plans">
      <div className="plans-header">
        <h2>Vendor Subscription Plans</h2>
        <p>Choose the perfect plan to showcase your products and grow your business</p>
        
        <div className="billing-toggle">
          <button 
            className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button 
            className={`billing-option ${billingCycle === 'yearly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly
            <span className="discount-badge">Save 17%</span>
          </button>
        </div>
      </div>

      <div className="plans-grid">
        {subscriptionPlans.map(plan => {
          const isCurrentPlan = currentPlan === plan.id
          const price = billingCycle === 'yearly' ? getAnnualPrice(plan.price) : plan.price
          const billingText = billingCycle === 'yearly' ? 'per year' : 'per month'
          
          return (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current' : ''}`}
              style={{ '--plan-color': plan.color } as React.CSSProperties}
            >
              {plan.popular && (
                <div className="popular-badge">Most Popular</div>
              )}
              
              {isCurrentPlan && showCurrentPlan && (
                <div className="current-plan-badge">Current Plan</div>
              )}

              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-pricing">
                  <span className="plan-price">${price}</span>
                  <span className="plan-period">{billingText}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="annual-savings">
                    Save ${(plan.price * 12) - price} annually
                  </div>
                )}
              </div>

              <div className="plan-features">
                <h4>Features Included:</h4>
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="feature-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="plan-limits">
                <h4>Plan Limits:</h4>
                <div className="limits-grid">
                  <div className="limit-item">
                    <span className="limit-label">Products:</span>
                    <span className="limit-value">
                      {plan.limits.products === 'unlimited' ? 'Unlimited' : plan.limits.products}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Featured:</span>
                    <span className="limit-value">
                      {plan.limits.featuredProducts === 999 ? 'Unlimited' : plan.limits.featuredProducts}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Images:</span>
                    <span className="limit-value">
                      {plan.limits.images === 'unlimited' ? 'Unlimited' : plan.limits.images}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Promotions:</span>
                    <span className="limit-value">
                      {plan.limits.salePromotions === 999 ? 'Unlimited' : `${plan.limits.salePromotions}/month`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="plan-action">
                {isCurrentPlan ? (
                  <button className="plan-button current" disabled>
                    Current Plan
                  </button>
                ) : (
                  <button 
                    className="plan-button" 
                    onClick={() => handlePlanSelect(plan.id)}
                  >
                    {currentPlan ? 'Switch to This Plan' : 'Get Started'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="plans-footer">
        <div className="footer-content">
          <h3>Why Choose Our Platform?</h3>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">🎯</span>
              <h4>Targeted Audience</h4>
              <p>Reach kratom enthusiasts actively looking for quality products</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">⭐</span>
              <h4>Verified Reviews</h4>
              <p>Build trust with our verified customer review system</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">📈</span>
              <h4>Analytics Insights</h4>
              <p>Track performance and optimize your listings with detailed analytics</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🛡️</span>
              <h4>Compliance Support</h4>
              <p>Stay compliant with our legal guidance and platform standards</p>
            </div>
          </div>
          
          <div className="contact-info">
            <p>Need a custom plan for your business? <a href="mailto:vendors@unbiasedkratom.com">Contact our vendor success team</a></p>
          </div>
        </div>
      </div>

      <style>{`
        .vendor-subscription-plans {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .plans-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .plans-header h2 {
          color: #2c5530;
          font-size: 2.5em;
          margin-bottom: 10px;
        }

        .plans-header p {
          color: #a0aec0;
          font-size: 1.1em;
          margin-bottom: 30px;
        }

        .billing-toggle {
          display: inline-flex;
          background: #f0f0f0;
          border-radius: 8px;
          padding: 4px;
        }

        .billing-option {
          padding: 12px 20px;
          border: none;
          background: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          position: relative;
          transition: all 0.3s ease;
        }

        .billing-option.active {
          background: #2d3748;
          color: #2c5530;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .discount-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ff6b6b;
          color: white;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 8px;
          font-weight: 600;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }

        .plan-card {
          background: #2d3748;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          overflow: hidden;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 2px solid transparent;
        }

        .plan-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.15);
        }

        .plan-card.popular {
          border-color: var(--plan-color);
          transform: scale(1.05);
        }

        .plan-card.current {
          border-color: #4caf50;
        }

        .popular-badge {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          background: var(--plan-color);
          color: white;
          padding: 8px 24px;
          border-radius: 0 0 12px 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .current-plan-badge {
          position: absolute;
          top: 0;
          right: 0;
          background: #4caf50;
          color: white;
          padding: 8px 16px;
          border-radius: 0 0 0 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .plan-header {
          padding: 30px 30px 20px;
          text-align: center;
          background: linear-gradient(135deg, var(--plan-color), var(--plan-color)dd);
          color: white;
        }

        .plan-name {
          font-size: 1.5em;
          margin-bottom: 15px;
        }

        .plan-pricing {
          margin-bottom: 10px;
        }

        .plan-price {
          font-size: 3em;
          font-weight: bold;
        }

        .plan-period {
          font-size: 14px;
          opacity: 0.9;
          margin-left: 8px;
        }

        .annual-savings {
          font-size: 12px;
          background: rgba(255,255,255,0.2);
          padding: 4px 12px;
          border-radius: 12px;
          display: inline-block;
        }

        .plan-features {
          padding: 25px 30px;
        }

        .plan-features h4 {
          color: #2c5530;
          margin-bottom: 15px;
          font-size: 16px;
        }

        .plan-features ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .plan-features li {
          padding: 8px 0;
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .feature-check {
          color: #4caf50;
          font-weight: bold;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .plan-limits {
          padding: 0 30px 25px;
        }

        .plan-limits h4 {
          color: #2c5530;
          margin-bottom: 15px;
          font-size: 16px;
        }

        .limits-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .limit-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fa;
          padding: 8px 12px;
          border-radius: 6px;
        }

        .limit-label {
          font-size: 13px;
          color: #a0aec0;
        }

        .limit-value {
          font-size: 13px;
          font-weight: 600;
          color: #2c5530;
        }

        .plan-action {
          padding: 25px 30px 30px;
        }

        .plan-button {
          width: 100%;
          padding: 15px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: var(--plan-color);
          color: white;
        }

        .plan-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .plan-button.current {
          background: #4caf50;
          cursor: not-allowed;
        }

        .plan-button.current:hover {
          transform: none;
          box-shadow: none;
        }

        .plans-footer {
          background: #f8fdf8;
          border-radius: 16px;
          padding: 40px;
          margin-top: 40px;
        }

        .footer-content h3 {
          text-align: center;
          color: #2c5530;
          font-size: 2em;
          margin-bottom: 30px;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
          margin-bottom: 30px;
        }

        .benefit-item {
          text-align: center;
        }

        .benefit-icon {
          font-size: 3em;
          display: block;
          margin-bottom: 15px;
        }

        .benefit-item h4 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .benefit-item p {
          color: #a0aec0;
          line-height: 1.5;
        }

        .contact-info {
          text-align: center;
          padding-top: 30px;
          border-top: 1px solid #ddd;
        }

        .contact-info a {
          color: #2c5530;
          text-decoration: none;
          font-weight: 600;
        }

        .contact-info a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .plans-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .plan-card.popular {
            transform: none;
          }
          
          .benefits-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .limits-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}